import Decimal from 'decimal.js';
import { CHART_COLORS, CO2_EQUIVALENT, TIME_UNIT_OPTIONS } from '@/consts';
import { unitToString } from './unitUtils';
import { stringify } from './miscUtils';
import { decodeNodeSelection } from './balanceUtils';

const isElectricity = unit => unit?.species === 'electricity' || unit?.measure === 'power';

const getTimeUnitLabel = timeUnit => {
  const matchingType = TIME_UNIT_OPTIONS.find(type => type.value === timeUnit);
  return matchingType ? matchingType.label : 'Year';
};

const parseWaterfallData = data => {
  let total = 0;

  data.forEach((item, index, arr) => {
    total += item.value;

    const name = typeof item.name === 'string' ? item.name.split(' ') : item.name;
    item.name = name;

    item.backgroundColor = item.backgroundColor ?? '#c3d9fc';

    if (index === 0) {
      item.start = 0;
      item.end = item.value;
    }

    if (index > 0 && index < arr.length - 1) {
      item.start = arr[index - 1].end;
      item.end = item.start + item.value;
    }

    if (index === arr.length - 1) {
      item.start = 0;
      item.end = total;
      item.value = total;
    }

    if (item.value < 0) {
      item.backgroundColor = item.negativeBgColor ?? '#c3d9fc';
    } else {
      item.backgroundColor = item.positiveBgColor ?? '#c3d9fc';
    }
  });

  return data;
};

export const parseTEAGraphData = teaData => {
  const normalizedData = teaData.map(({ label, cost }) => ({
    name: label,
    value: cost,
    negativeBgColor: '#B2F0DC',
    positiveBgColor: '#FFBC71',
  }));

  const totalData = {
    name: 'Total',
    value: 0,
    positiveBgColor: '#E8750F',
  };

  normalizedData.push(totalData);

  return parseWaterfallData(normalizedData);
};

export const parseCashFlowsData = data => {
  if (!data?.cash_flows) {
    return null;
  }

  const cashFlowData = data?.cash_flows;

  return { data: cashFlowData };
};

const findColumn = (columns, category, subCategory) =>
  columns?.find(col => col.category?.[0] === category && col.category?.[1] === subCategory);

const findFinancialResult = (financialResults, label) => financialResults?.find(item => item.label === label);

export const parseFinancialDeepDive = (cashFlowColumns, financialResults, levelizedTotal) => {
  if (!cashFlowColumns) {
    return null;
  }

  const yearCol = findColumn(cashFlowColumns, 'Indicators', 'Year');
  const opYearCol = findColumn(cashFlowColumns, 'Indicators', 'Operation Year');
  const atpdCashFlowCol = findColumn(cashFlowColumns, 'Summary Cash Flows', 'After Tax Post Depreciation Cash Flow');
  const cumulativeCol = findColumn(
    cashFlowColumns,
    'Summary Cash Flows',
    'Cumulative After Tax Post Depreciation Cash Flow',
  );

  const labels = yearCol?.data ?? [];
  const operationYears = opYearCol?.data ?? [];
  const atpdSeries = atpdCashFlowCol?.data ?? [];
  const cumulative = cumulativeCol?.data ?? [];

  const incomeRows = [
    { key: 'revenues', label: 'Revenues', column: findColumn(cashFlowColumns, 'Revenues', 'Revenues') },
    { key: 'capital', label: 'Capital costs', column: findColumn(cashFlowColumns, 'Capital Costs', 'Total') },
    { key: 'operating', label: 'Operating costs', column: findColumn(cashFlowColumns, 'Operating Costs', 'Total') },
    {
      key: 'depreciation',
      label: 'Depreciation',
      column: findColumn(cashFlowColumns, 'Income and Taxes', 'Depreciation'),
    },
    { key: 'taxes', label: 'Total taxes', column: findColumn(cashFlowColumns, 'Income and Taxes', 'Total Taxes') },
    { key: 'ebitda', label: 'EBITDA', column: findColumn(cashFlowColumns, 'Summary Cash Flows', 'EBITDA') },
    { key: 'ebit', label: 'EBIT', column: findColumn(cashFlowColumns, 'Summary Cash Flows', 'EBIT') },
    { key: 'nopat', label: 'NOPAT', column: findColumn(cashFlowColumns, 'Summary Cash Flows', 'NOPAT') },
    {
      key: 'netIncome',
      label: 'Net income',
      column: findColumn(cashFlowColumns, 'Summary Cash Flows', 'Net Income'),
      emphasize: true,
    },
    {
      key: 'ufcf',
      label: 'Unlevered free cash flow',
      column: findColumn(cashFlowColumns, 'Summary Cash Flows', 'Unlevered Free Cash Flows'),
      emphasize: true,
    },
  ]
    .filter(row => row.column)
    .map(row => ({ ...row, data: row.column.data, unit: row.column.unit }));

  const kpis = {
    lcoh: levelizedTotal ?? null,
    npv: findFinancialResult(financialResults, 'Project Net Present Value (NPV)')?.quantity ?? null,
    irrReal: findFinancialResult(financialResults, 'Project After-Tax, Real IRR')?.quantity ?? null,
    irrNominal: findFinancialResult(financialResults, 'Project After-Tax, Nominal IRR')?.quantity ?? null,
    hurdle: findFinancialResult(financialResults, 'Hurdle Rate (After-Tax, Real)')?.quantity ?? null,
    payback: findFinancialResult(financialResults, 'Payback Period')?.quantity ?? null,
  };

  return {
    labels,
    operationYears,
    cumulative,
    annualNetCashFlow: atpdSeries,
    cashFlowUnit: atpdCashFlowCol?.unit ?? null,
    incomeRows,
    kpis,
  };
};

export const parseTEAData = teaData => {
  return parseTEAGraphData(teaData.levelized_costs);
};

export const parseLCAData = lcaData => {
  const { emissions } = lcaData;
  const data = emissions.map(emission => ({
    name: emission.label,
    value: emission.quantity.scalar,
    type: emission.type,
  }));

  const totalData = {
    name: 'Total',
    value: 0,
    positiveBgColor: '#2d5cf6',
  };

  data.push(totalData);

  return parseWaterfallData(data);
};

export const parseLCABreakdownData = parsedLCAData => {
  return parsedLCAData
    .map(item => {
      return {
        label: item.name.join(' '),
        amount: new Decimal(item.value),
      };
    })
    .filter(item => !item.label.match(/Scope|Total/));
};

export const parsePathwayCostBreakdownData = pathwayCostBreakdown =>
  pathwayCostBreakdown.map(({ label, levelized_contribution }) => ({
    label,
    amount: new Decimal(levelized_contribution.scalar),
    unit: unitToString(levelized_contribution.unit),
  }));

export const parsePathwayEmissionsBreakdownData = pathwayEmissionsBreakdown =>
  pathwayEmissionsBreakdown.map(({ label, quantity }) => ({
    label,
    amount: new Decimal(quantity.scalar),
  }));

export const parseLCABreakdownByScope = (parsedLCAData, scope) => {
  return parsedLCAData
    .filter(item => item.type === `scope${scope}`)
    .map(item => ({
      label: item.name.join(' '),
      amount: new Decimal(item.value),
    }));
};

export const parseLCABreakdownFormData = feedstockCosts => {
  return feedstockCosts.reduce((acc, item) => {
    // #TODO: remove this once we have a better way to identify these items
    if (/Stream Mixer|Stream Splitter|Power Infrastructure Emissions/.test(item.description)) {
      return acc;
    }

    const { description, levelized_contribution } = item;
    const { scalar, unit } = levelized_contribution;
    const value = new Decimal(scalar);

    const resultItem = {
      label: description,
      amount: value,
      unit: unitToString(unit),
    };
    acc.push(resultItem);

    return acc;
  }, []);
};

const parseBalancedItem = (item, timeUnitLabel) => {
  const { quantity } = item;
  const { scalar, unit } = quantity;

  return {
    ...item,
    quantity: scalar,
    unit: isElectricity(unit) ? unitToString(unit) : `${unitToString(unit)}/${timeUnitLabel.toLocaleLowerCase()}`,
  };
};

const parseExtraItem = item => {
  const { label, quantity } = item;
  const { scalar, unit } = quantity;
  const value = new Decimal(scalar);

  return {
    label,
    quantity: value,
    unit: unitToString(unit),
  };
};

export const parseBalanceData = (data, nodesById, params) => {
  const { node_id, parent_node_id, timeframe = 'year' } = params;
  // internal pathway nodes are not in `nodesById`, so fall back to their parent
  const node = nodesById[parent_node_id ?? node_id];
  const emissions = node?.emission_types ?? [];
  const emission = emissions.find(({ name, co2_equivalent }) => {
    return name === params.emission_type && co2_equivalent === params.co2_equivalent;
  });

  const { inputs = [], outputs = [], extra = [], emissions: lcaEmissions = [] } = data;

  const scopes = lcaEmissions.map(({ label, quantity }) => ({
    label: label,
    quantity: { scalar: new Decimal(quantity.scalar).dividedBy(1000), unit: { label: 'ton' } },
  }));

  const timeUnitLabel = getTimeUnitLabel(timeframe);

  let sections = [
    {
      header: 'Feedstocks',
      rows: inputs.map(item => parseBalancedItem(item, timeUnitLabel)),
    },
    {
      header: 'Products',
      rows: outputs.map(item => parseBalancedItem(item, timeUnitLabel)),
    },
    // only scope 1 is attributable to a node inside an internal pathway, so the
    // backend omits emissions for those and there is no section to show
    ...(parent_node_id
      ? []
      : [
          {
            header: emission ? `${emission.label} Emissions` : 'CO₂ Equivalent Emissions',
            rows: scopes.map(item => parseBalancedItem(item, timeUnitLabel)),
          },
        ]),
  ];

  if (extra.length > 0) {
    sections = [
      {
        header: 'Key Metrics',
        rows: extra.map(item => parseExtraItem(item)),
      },
      ...sections,
    ];
  }

  return sections;
};

const parseCharacteristicsItem = item => {
  const { label, quantity } = item;
  const { scalar, unit } = quantity;
  const value = new Decimal(scalar);

  return {
    label,
    amount: value,
    unit: unitToString(unit),
  };
};

const parseLevelizedCostBreakdownItem = item => {
  const { label, cost, children } = item;
  const value = new Decimal(cost);

  return {
    label,
    amount: value,
    children: children?.map(child => parseLevelizedCostBreakdownItem(child)) ?? [],
  };
};

export const parseCharacteristicsData = data => {
  const { facility_characteristics = [], total_costs = [] } = data;

  const sections = [
    {
      header: 'Asset characteristics',
      rows: facility_characteristics?.map(item => parseCharacteristicsItem(item)),
    },
    {
      header: 'Total asset costs',
      rows: total_costs?.map(item => parseCharacteristicsItem(item)),
    },
  ];

  return sections;
};

export const parseCostBreakdownData = data => {
  const { levelized_costs = [], levelized_total } = data;

  const sections = [
    {
      header: 'Levelized Cost Breakdown',
      rows: levelized_costs?.map(item => parseLevelizedCostBreakdownItem(item)),
      total: new Decimal(levelized_total ?? 0),
    },
  ];

  return sections;
};

export const parseCharacteristicsFormData = plotFormData => {
  const { nodeId } = plotFormData;

  const params = {
    node_id: nodeId,
  };

  return params;
};

export const parseCostBreakdownFormData = plotFormData => {
  const { nodeId, levelizeBy, levelizeUnit } = plotFormData;
  const params = {
    node_id: nodeId,
    depth: 2,
  };

  if (levelizeUnit) {
    const { io_name, io_type } = JSON.parse(levelizeBy);

    params.levelization = {
      io_name,
      io_type,
      unit: JSON.parse(levelizeUnit),
    };
  }

  return params;
};

export const parseMCData = data => data;

export const getLevelizeByLabel = (node, inputOutput) => {
  const { io_name, io_type } = JSON.parse(inputOutput);
  const output = node[`${io_type}s`].find(io => io.name === io_name);

  return output?.type?.label;
};

export const getLCAYaxisLabel = plot => {
  if (plot.levelize !== 'true') {
    return 'kgCO₂e/year';
  } else {
    const emissionType = plot?.emissionType ? JSON.parse(plot?.emissionType) : {};
    let { label, unit } = emissionType;

    const { levelizeUnit } = plot;
    const unitObj = JSON.parse(levelizeUnit);
    label = label === 'CO₂ Equivalent' ? 'CO₂e' : label;

    return `${unitToString(unit)}${label}/${unitToString(unitObj)}`;
  }
};

export const getTEAYaxisLabel = (plot, node, showLevelizedLabel = true) => {
  const { levelizeUnit, levelizeBy } = plot;
  const unitObj = JSON.parse(levelizeUnit);
  const levelizeLabel = getLevelizeByLabel(node, levelizeBy);

  if (showLevelizedLabel) {
    return `$/${unitToString(unitObj)} ${levelizeLabel}`;
  }

  return `$/${unitToString(unitObj)}`;
};

export const getSensitivityXaxisLabel = (plot, node, type) => {
  const { levelizeUnit, levelizeBy } = plot;
  const unitObj = JSON.parse(levelizeUnit);
  const levelizeLabel = getLevelizeByLabel(node, levelizeBy);
  const label = `${unitToString(unitObj)} ${levelizeLabel}`;

  return type === 'tea' ? `$/${label}` : `kgCO₂e/${label}`;
};

export const getMCXaxisLabel = (distribution, unit, scalar) => {
  if (distribution === 'emissions') {
    return `Levelized Emissions [${unit}]`;
  }

  if (unit.match(/electricity/i) && scalar === 1000) {
    unit = unit.replace('kWh', 'MWh');
  }

  return `Levelized Costs [${unit}]`;
};

export const parseLCAFormData = plotFormData => {
  const { nodeId, levelize, levelizeBy, levelizeUnit, emissionType, scope1Breakdown, scope3Breakdown, year } =
    plotFormData;
  const params = {
    node_id: nodeId,
  };

  if (year) {
    params.year = year;
  }

  if (scope1Breakdown) {
    params.scope1_breakdown = scope1Breakdown;
  }

  if (scope3Breakdown) {
    params.scope3_breakdown = scope3Breakdown;
    params.include_pathway_emissions_breakdown = true;
  }

  if (emissionType) {
    const { co2_equivalent, emission_type } = JSON.parse(emissionType);

    params.co2_equivalent = co2_equivalent;

    if (emission_type !== CO2_EQUIVALENT) {
      params.emission_type = emission_type;
    }
  }

  if (levelize === 'true') {
    const { io_name, io_type } = JSON.parse(levelizeBy);

    params.levelization = {
      io_name,
      io_type,
      unit: JSON.parse(levelizeUnit),
    };
  }

  return params;
};

export const parseLevelization = levelization => {
  const { unit, io_type, io_name } = levelization;
  return {
    levelizeUnit: stringify(unit),
    levelizeBy: stringify({ io_name, io_type }),
  };
};

export const parseTEAFormData = plotFormData => {
  const { nodeId, levelizeBy, levelizeUnit, upstreamCostsBreakdown } = plotFormData;
  const params = {
    node_id: nodeId,
  };

  params.include_pathway_cost_breakdown = !!upstreamCostsBreakdown;

  if (levelizeUnit) {
    const { io_name, io_type } = JSON.parse(levelizeBy);

    params.levelization = {
      io_name,
      io_type,
      unit: JSON.parse(levelizeUnit),
    };
  }

  return params;
};

export const parseBalanceFormData = plotFormData => {
  const { nodeId, emissionType, timeframe } = plotFormData;
  const path = decodeNodeSelection(nodeId);

  const params = {
    node_id: path[path.length - 1],
    timeframe,
  };

  if (path.length > 1) {
    // a node inside an internal pathway: it has no LCA emissions of its own, so
    // the emission type doesn't apply
    params.parent_node_id = path[path.length - 2];
    return params;
  }

  if (emissionType) {
    const { emission_type, co2_equivalent } = JSON.parse(emissionType);

    params.co2_equivalent = co2_equivalent;

    if (emission_type !== CO2_EQUIVALENT) {
      params.emission_type = emission_type;
    }
  }

  return params;
};

export const parseMCFormData = plotFormData => {
  const { nodeId, levelize, levelizeBy } = plotFormData;
  const params = {
    node_id: nodeId,
  };

  if (levelize !== 'false' && levelizeBy) {
    const { io_name, io_type } = JSON.parse(levelizeBy);

    params.levelization = {
      io_name,
      io_type,
    };
  }

  return params;
};

export const sortNodesByAnchor = (nodes, anchorId) => {
  return nodes.sort((a, b) => {
    if (a.id === anchorId) return -1;
    if (b.id === anchorId) return 1;
    return 0;
  });
};

export const parseLCATimeseriesData = data => {
  const { cumulative, yearly, start_year, end_year } = data;
  return { cumulative, yearly, start_year, end_year };
};

export const parseSensitivityData = data => {
  const { sensitivities, baseline } = data;

  if (!sensitivities?.length) {
    return null;
  }

  const labels = sensitivities.map(s => s.param_label);

  const datasets = {};
  let indexColor = 0;

  sensitivities.forEach((s, index) => {
    if (!datasets[`min-${s.node_id}`]) {
      datasets[`min-${s.node_id}`] = {
        label: s.node_label,
        nodeId: s.node_id,
        data: new Array(labels.length).fill(0),
        backgroundColor: CHART_COLORS[indexColor],
        borderColor: CHART_COLORS[indexColor],
        borderWidth: 1,
        customData: new Array(labels.length).fill(0),
      };
    }

    if (!datasets[`max-${s.node_id}`]) {
      datasets[`max-${s.node_id}`] = {
        label: s.node_label,
        nodeId: s.node_id,
        data: new Array(labels.length).fill(0),
        backgroundColor: CHART_COLORS[indexColor],
        borderColor: CHART_COLORS[indexColor],
        borderWidth: 1,
        customData: new Array(labels.length).fill(0),
      };

      indexColor += 1;
    }

    datasets[`max-${s.node_id}`].data[index] = new Decimal(s.max).minus(baseline).toNumber();
    datasets[`min-${s.node_id}`].data[index] = new Decimal(s.min).minus(baseline).toNumber();
    datasets[`max-${s.node_id}`].customData[index] = {
      paramValue: new Decimal(s.max_param_value).toNumber(),
      baselineParamValue: new Decimal(s.baseline_param_value).toNumber(),
    };
    datasets[`min-${s.node_id}`].customData[index] = {
      paramValue: new Decimal(s.min_param_value).toNumber(),
      baselineParamValue: new Decimal(s.baseline_param_value).toNumber(),
    };
  });

  return { datasets: Object.values(datasets), labels, baseline };
};

const parseSensitivityFormData = (formData, type) => {
  const { nodeId, levelizeBy, levelizeUnit } = formData;
  const params = {
    node_id: nodeId,
    analysis_type: type,
  };

  if (levelizeUnit) {
    const { io_name, io_type } = JSON.parse(levelizeBy);

    params.levelization = {
      io_name,
      io_type,
      unit: JSON.parse(levelizeUnit),
    };
  }

  return params;
};

export const parseSensitivityLCAFormData = formData => {
  return parseSensitivityFormData(formData, 'lca');
};

export const parseSensitivityTEAFormData = formData => {
  return parseSensitivityFormData(formData, 'tea');
};

export const getLcohUnit = levelizeUnit => {
  if (!levelizeUnit) return '$';
  try {
    return `$/${unitToString(JSON.parse(levelizeUnit))}`;
  } catch {
    return '$';
  }
};

export const getYearOptions = (startYear, endYear) => {
  const options = [{ value: '', label: 'Lifetime average' }];

  for (let year = startYear; year <= endYear; year++) {
    options.push({ value: year, label: year });
  }

  return options;
};

const quantity = q => (q ? { value: q.scalar, unit: unitToString(q.unit) } : null);

/**
 * The capture-and-handling chain, flattened for display. Costs arrive levelized
 * by the CO2 itself, so unlike the other TEA results they are not expressed per
 * unit of the analyzed node's product.
 */
export const parseCarbonChainData = data => {
  if (!data || !data.stages?.length) {
    return null;
  }

  const total = quantity(data.total);
  const stages = data.stages.map(stage => ({
    nodeId: stage.node_id,
    label: stage.label,
    capture: stage.capture,
    cost: quantity(stage.cost),
    co2In: quantity(stage.co2_in),
    co2Out: quantity(stage.co2_out),
    co2Lost: quantity(stage.co2_lost),
    share: total?.value ? stage.cost.scalar / total.value : null,
  }));

  return {
    total,
    stages,
    captured: quantity(data.captured),
    stored: quantity(data.stored),
    co2Lost: quantity(data.co2_lost),
    captureIncludedInPlantCosts: data.capture_included_in_plant_costs,
    products: (data.products ?? []).map(product => ({
      ioName: product.io_name,
      label: product.label,
      cost: quantity(product.cost),
      levelizedCost: quantity(product.levelized_cost),
      share: product.share,
    })),
  };
};
